import { useState, useMemo, useEffect } from "react";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Loader } from "../Loader";
import { H3 } from "../Heading";
import { Paragraph } from "../Paragraph";
import { Button } from "../Button";
import { notify } from "../../utils/notify";
import { Modal } from "../Modal";
import { useForms } from "../../data/useForms";
import { Toggle } from "../Toggle";
import { Form, Formik } from "formik";
import { showError } from "../../utils/error-handling";
import { MandateTypeForm } from "../../types/Forms";
import { ConfirmModal } from "../Modals/ConfirmModal";
import success from "../../assets/card-success-icon.svg";
import { Image } from "../Image";
import { queryClient } from "../../queryClient";
import { useExtraAction } from "../../data/useExtraAction";
import { useAddExtraAction } from "../../data/useAddExtraAction";
import { useGetExtraAction } from "../../data/useGetExtraAction";

interface FormListProps {
  isOpen: boolean;
  onRequestClose: () => void;
  mandateId: number;
  individualId: number;
  includedForms: MandateTypeForm[];
  type?: "individual" | "entity";
}

export const MandateFormList = ({
  isOpen,
  onRequestClose,
  mandateId,
  individualId,
  includedForms,
  type,
}: FormListProps) => {
  const { data, isLoading: isLoadingForms } = useForms(type);

  const { data: extraActionList } = useGetExtraAction(individualId);

  const { mutate: addExtraAction } = useAddExtraAction();
  const { mutate: extraAction } = useExtraAction();

  const addedForms = extraActionList?.data?.form;

  // Local copy of included forms so we can update UI optimistically
  const [localIncluded, setLocalIncluded] =
    useState<MandateTypeForm[]>(includedForms);
  const [activeForms, setActiveForms] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Keep localIncluded in sync if prop changes
  useEffect(() => {
    setLocalIncluded(includedForms);
  }, [includedForms]);

  // Filter out included forms from data
  const filteredForms = useMemo(() => {
    if (!data || !addedForms) return [];
    return data.filter(
      (form) =>
        (form.has_question || form.has_section) &&
        !addedForms.some((added) => added.id === form.id)
    );
  }, [data, addedForms]);

  const handleActiveChange = (id: number, isAdded: boolean) => {
    setActiveForms((prev) => {
      if (isAdded) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        return prev.filter((fid) => fid !== id);
      }
    });
  };

  const getInitialValues = () => {
    if (!filteredForms) return {};
    const initial: Record<string, boolean> = {};
    filteredForms.forEach((item) => {
      initial[`active_${item.id}`] = false;
    });
    return initial;
  };

  const handleSubmit = () => {
    const combinedIds = [
      ...localIncluded.map((item) => item.id),
      ...activeForms, // assuming activeForms is an array of IDs
    ];

    // Deduplicate using Set
    const uniqueIds = Array.from(new Set(combinedIds));

    const obj = {
      _form: uniqueIds,
    };

    addExtraAction(
      {
        id: individualId,
        data: {
          ...obj,
        },
      },
      {
        onSuccess: () => {
          addRequestedForms();
        },
        onError: (err) => showError(err, "Error occurred while adding form(s)"),
      }
    );
  };

  const addRequestedForms = () => {
    extraAction(
      { action: "add_form", id: individualId },
      {
        onError: (err) =>
          showError(err, "A problem occurred while adding new form(s)."),
        onSuccess: () => {
          // update localIncluded to include new forms and reset selection
          const newlyAdded = data
            .filter((f) => activeForms.includes(f.id))
            .map((f) => ({ _form: f, id: f.id, order: 0 }));
          setLocalIncluded((prev) => [...prev, ...newlyAdded]);
          setActiveForms([]);
          setSelectAll(false);
          setIsConfirmModalOpen(false);
          notify("Form(s) added successfully!", { type: "success" });
          queryClient.invalidateQueries(["extra_action", individualId]);
          queryClient.invalidateQueries(["individual"]);
        },
      }
    );
  };

  return (
    <>
      <Modal
        height="70%"
        width="700px"
        isOpen={isOpen}
        onClose={onRequestClose}
      >
        <H3>Forms</H3>

        <H3 fontSize={3}>Existing Forms</H3>
        <Paragraph color="gray.60" fontSize={1}>
          Selected {addedForms?.length}
        </Paragraph>

        <Box mb={3}>
          {localIncluded.length === 0 ? (
            <Paragraph color="gray.60" fontSize={1}>
              No forms have been added to this application type yet
            </Paragraph>
          ) : (
            addedForms?.map((item, index) => (
              <Flex
                key={index}
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid"
                borderBottomColor="gray.10"
                py={2}
              >
                <Box>
                  <H3 fontSize={2} mb="0px">
                    {item.name}
                  </H3>
                  <Paragraph my={0} color="gray.60" fontSize={1}>
                    {item.description}
                  </Paragraph>
                </Box>
                <Image
                  src={success}
                  alt="icon"
                  height="20px"
                  width="auto"
                  mr={1}
                />
              </Flex>
            ))
          )}
        </Box>

        <H3 mt={3} fontSize={3}>
          Add new forms
        </H3>
        <Paragraph color="gray.60" fontSize={1}>
          You can add forms to this application. These forms will need to be
          complete by your customer as part of the verification process.
        </Paragraph>

        {isLoadingForms ? (
          <Flex justifyContent="center" alignItems="center" minHeight="25vh">
            <Loader />
          </Flex>
        ) : (
          <Formik
            initialValues={getInitialValues()}
            enableReinitialize
            validate={() => null}
            onSubmit={() => {}}
          >
            {(props) => (
              <Form>
                <Flex justifyContent="space-between" alignItems="center" mt={3}>
                  <Paragraph fontSize={1} color="gray.60">
                    Selected {activeForms.length}
                  </Paragraph>
                  <Flex alignItems="center" gap="2">
                    <Paragraph fontSize={1} color="gray.60" mr={2}>
                      Select all
                    </Paragraph>
                    <Toggle
                      isChecked={selectAll}
                      isDisabled={filteredForms.length === 0}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectAll(checked);
                        filteredForms.forEach((item) => {
                          props.setFieldValue(`active_${item.id}`, checked);
                          handleActiveChange(item.id, checked);
                        });
                      }}
                    />
                  </Flex>
                </Flex>

                <Box>
                  {filteredForms.map((item) => (
                    <Flex
                      key={item.id}
                      justifyContent="space-between"
                      alignItems="center"
                      borderBottom="1px solid"
                      borderBottomColor="gray.10"
                      py={2}
                    >
                      <Box>
                        <H3 fontSize={2} mb="0px">
                          {item.name}
                        </H3>
                        <Paragraph my={0} color="gray.60" fontSize={1}>
                          {item.description}
                        </Paragraph>
                      </Box>
                      <Toggle
                        isChecked={props.values[`active_${item.id}`] || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          props.setFieldValue(`active_${item.id}`, checked);
                          handleActiveChange(item.id, checked);
                        }}
                        mt={2}
                      />
                    </Flex>
                  ))}
                  {filteredForms.length === 0 && (
                    <Paragraph fontSize={1}>
                      No further forms available
                    </Paragraph>
                  )}
                </Box>

                <Flex py={4} justifyContent="end">
                  <Button
                    type="button"
                    size="regular"
                    isDisabled={!props.dirty || activeForms.length === 0}
                    onClick={() => {
                      onRequestClose();
                      setIsConfirmModalOpen(true);
                    }}
                  >
                    Next
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onRequestClose={() => {
          setActiveForms([]);
          setSelectAll(false);
          setIsConfirmModalOpen(false);
        }}
        handleConfirm={handleSubmit}
        title="Request form(s)?"
        content="An email will be sent to the individual asking them to complete the forms through the mobile app."
      />
    </>
  );
};
